import Dexie, { Table } from 'dexie';

import { Issue, Article, Style } from '../constants'

export class DexieDb extends Dexie implements Db {
    issues!: Table<Issue>;
    articles!: Table<Article>;
    styles!: Table<Style>;
    constructor() {
        super('canvasflow');
        this.version(1).stores({
            issues: '&id, index, slug',
            articles: '&id, index, IssueID, slug, isFeatured',
            styles: '&id',
        });
    }


    start(): Promise<void> {
        return Promise.resolve();
    }

    async saveIssues(issues: Array<any>): Promise<Array<Issue>> {
        for (let i = 0; i < issues.length; i++) {
            const issue = issues[i];
            issue.index = i;
            await this.saveIssue(issue);
        }
        return this.getIssues();
    }

    async getIssues(): Promise<Array<Issue>> {
        const issues = await this.issues.toArray();
        return issues.sort((a, b) => a.index - b.index);
    }

    async getIssue(id: string, isSlug?: boolean): Promise<Issue | null> {
        const cachedIssue = isSlug
            ? await this.issues.get({ slug: id })
            : await this.issues.get({ id });
        if (!cachedIssue) {
            return null;
        }
        return cachedIssue;
    }

    async saveIssue(issue: any): Promise<Issue | null> {
        if (issue.articles) {
            await this.deleteArticlesByIssue(issue.id);
            issue.articles = issue.articles.map((article: any) => {
                article.IssueID = `${issue.id}`;
                return article;
            });
            await this.saveArticles(clone(issue.articles));
            delete issue.articles;
        }
        await this.issues.put(issue, 'id');
        const cachedIssue = await this.getIssue(issue.slug, true);
        if (cachedIssue) {
            cachedIssue.articles = await this.getArticlesByIssue(
                cachedIssue.id,
            );
        }
        return cachedIssue;
    }

    async deleteArticlesByIssue(id: string): Promise<void> {
        await this.articles.where('IssueID').equals(id).delete();
    }

    async deleteArticle(id: string): Promise<void> {
        await this.articles.where('id').equals(id).delete();
    }

    async saveArticles(articles: Array<any>): Promise<void> {
        Logger.debug(`ARTICLES`, articles);
        await this.articles.bulkPut(articles);
    }

    async saveFeatureArticles(articles: Array<Article>): Promise<void> {
        Logger.debug(`FEATURED ARTICLES`, articles);
        /* Remove existing feature articles in the issue */
        const issuesSet = new Set<string>([]);
        for (const article of articles) {
            if (article.IssueID) {
                issuesSet.add(`${article.IssueID}`);
            }
        }
        await this.removeFeatureArticleInIssue([...issuesSet]);
        /**************************************************/

        const featureArticles = await this.getFeatureArticles();
        const featureArticlesMap = new Map<string, Article>();
        for (const article of featureArticles) {
            featureArticlesMap.set(article.id, article)
        }
        const response = [];
        for (const article of articles) {
            const { featureImage, name, slug, thumbnail } = article;
            if (featureArticlesMap.has(article.id)) {
                const cachedArticle = featureArticlesMap.get(article.id);
                if (!cachedArticle) {
                    continue;
                }
                cachedArticle.featureImage = featureImage;
                cachedArticle.name = name;
                cachedArticle.slug = slug;
                cachedArticle.thumbnail = thumbnail;
                response.push(cachedArticle);
            } else {
                response.push(article);
            }
        }
        await this.articles.bulkPut(response);
    }

    async removeFeatureArticleInIssue(issues: Array<string>) {
        for (const issue of issues) {
            await this.articles.where('IssueID')
                .equals(`${issue}`)
                .filter((a: Article) => a.isFeatured)
                .delete();
        }
    }

    async getArticlesByIssue(id: string): Promise<Array<Article>> {
        return this.articles.where('IssueID').equals(`${id}`).toArray();
    }

    async getFeatureArticles(): Promise<Article[]> {
        return await this.articles.filter(a => a.isFeatured).toArray();
    }

    async getArticle(id: string, isSlug?: boolean): Promise<Article | null> {
        const cachedArticle = isSlug
            ? await this.articles.get({ slug: id })
            : await this.articles.get({ id });
        if (!cachedArticle) {
            return null;
        }
        return cachedArticle;
    }

    async removeStyles(): Promise<void> {
        await this.styles.clear();
    }

    async saveStyles(styles: any[]): Promise<void> {
        styles = uniqueBy(styles, 'id');
        Logger.debug(`STYLES`, styles);
        await this.styles.clear();
        await this.styles.bulkPut(styles);
    }

    async getStyles(): Promise<Style[]> {
        return await this.styles.toArray();
    }

    async getStyle(id: string): Promise<Style | null> {
        const style = await this.styles.get({ id });
        if (!style) {
            return null;
        }
        return style;
    }

    async clear(): Promise<void> {
        this.issues.clear();
        this.articles.clear();
        this.styles.clear();
    }
}

export interface Db {
    start(): Promise<void>;
    clear(): Promise<void>;
    saveIssues(issues: Array<any>): Promise<Array<Issue>>;
    getIssues(): Promise<Array<Issue>>;
    getIssue(id: string, isSlug?: boolean): Promise<Issue | null>;
    saveIssue(issue: any): Promise<Issue | null>;
    deleteArticlesByIssue(id: string): Promise<void>;
    deleteArticle(id: string): Promise<void>;
    saveArticles(articles: Array<any>): Promise<void>;
    saveFeatureArticles(articles: Array<Article>): Promise<void>;
    getFeatureArticles(): Promise<Array<Article>>;
    getArticlesByIssue(id: string): Promise<Array<Article>>;
    getArticle(id: string, isSlug?: boolean): Promise<Article | null>;
    removeStyles(): Promise<void>;
    saveStyles(styles: Array<any>): Promise<void>;
    getStyles(): Promise<Array<Style>>;
    getStyle(id: string): Promise<Style | null>;
}

function uniqueBy(inputs: Array<any>, property: any) {
    const response = [];
    const mapResponse: any = {};
    for (const input of inputs) {
        mapResponse[`${input[property]}`] = input;
    }

    for (const input in mapResponse) {
        response.push(mapResponse[input]);
    }

    return response;
}

class Logger {
    static prefix = 'Db';
    static log(...msg: any) {
        const color = '#bf5af2';
        console.log(`%c${Logger.prefix}`, Logger.logStyle(color), ...msg);
    }
    static info(...msg: any) {
        const color = '#fed709';
        console.info(`%c${Logger.prefix}`, Logger.logStyle(color), ...msg);
    }

    static debug(...msg: any) {
        const color = '#5e5ce6';
        console.info(`%c${Logger.prefix}`, Logger.logStyle(color), ...msg);
    }

    static error(...msg: any) {
        const color = '#ff375f';
        console.info(`%c${Logger.prefix}`, Logger.logStyle(color), ...msg);
    }
    static logStyle(color: string) {
        return `color: white; background: ${color}; border-radius: 6px; padding: 2px 12px;`;
    }
}

function clone(obj: any) {
    if (structuredClone) {
        return structuredClone(obj);
    }
    return obj ? JSON.parse(JSON.stringify(obj)) : obj;
}